import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useT } from "@/i18n/LanguageContext";
import { useSEO } from "@/lib/useSEO";
import { DisciplineSwitcher } from "@/components/academy/DisciplineSwitcher";
import { DISCIPLINE_THEME, BTN_FX } from "@/lib/academyTheme";
import {
  learnerId as logicLearnerId, fetchCourse as fetchLogicCourse,
  deleteLessonWork as deleteLogicLessonWork, deleteRecord as deleteLogicRecord, loc,
} from "@/lib/logicApi";
import {
  learnerId as physicsLearnerId, fetchCourse as fetchPhysicsCourse,
  deleteLessonWork as deletePhysicsLessonWork, deleteRecord as deletePhysicsRecord,
} from "@/lib/physicsApi";
import {
  learnerId as ethicsLearnerId, fetchCourse as fetchEthicsCourse,
  deleteLessonWork as deleteEthicsLessonWork, deleteRecord as deleteEthicsRecord,
} from "@/lib/ethicsApi";

const API = process.env.REACT_APP_BACKEND_URL;

const lessonUrl = (item) =>
  `/academy/${item.discipline}/phase/${item.phaseSlug}/lesson/${item.lessonSlug}`;

function typeUrl(item) {
  const base = `/academy/${item.discipline}`;
  if (item.type === "lesson" && item.lessonSlug && item.phaseSlug) return lessonUrl(item);
  if (item.type === "record") return item.discipline === "logic" ? `${base}/journal` : `${base}/record`;
  if (item.type === "practice") return `${base}/practice`;
  if (item.type === "evidence") return item.discipline === "logic" ? `${base}/progress` : `${base}/path`;
  if (item.type === "chamber") return item.discipline === "logic" ? `${base}/chamber-of-judgment` : `${base}/chamber`;
  return base;
}

function fmt(ts, lang) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (isNaN(d)) return String(ts).slice(0, 10);
  return d.toLocaleDateString(lang === "nl" ? "nl-NL" : "en-GB", { day: "numeric", month: "short", year: "numeric" })
    + " · " + d.toLocaleTimeString(lang === "nl" ? "nl-NL" : "en-GB", { hour: "2-digit", minute: "2-digit" });
}

function WorkItem({ item, lessonMeta, onDelete }) {
  const { t, lang } = useT();
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const th = DISCIPLINE_THEME[item.discipline] || DISCIPLINE_THEME.logic;
  const meta = item.type === "lesson" ? lessonMeta[`${item.discipline}:${item.lessonSlug}`] : null;
  const title = item.type === "lesson"
    ? (meta ? loc(meta.title, lang) : item.lessonSlug)
    : item.type === "record" ? (item.recordName || "Record")
    : item.type === "practice" ? t("academy.mywork.type.practice")
    : item.type === "evidence" ? t("academy.mywork.type.evidence")
    : item.preview || t("academy.mywork.type.chamber");
  const statusTone = item.status === "completed" ? "border-gold/60 text-gold"
    : item.status === "inProgress" ? `border-current` : "border-white/25 text-bone/60";
  const canDelete = item.type === "lesson" || item.type === "record";
  const canContinue = item.type === "lesson" || item.type === "record" || item.status !== "completed";
  return (
    <div className={`border ${th.frame.split(" ")[1] ? "" : ""} border-white/10 bg-white/[0.02] hover:border-white/25 transition-colors`}
      data-testid={`mywork-item-${item.type}-${item.id}`}>
      <div className="p-5 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 border" style={{ color: th.accent, borderColor: `${th.accent}66` }}>
              {t(`academy.nav.name.${item.discipline}`)}
            </span>
            <span className="text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 border border-white/15 text-bone/60">
              {t(`academy.mywork.type.${item.type}`)}
            </span>
            <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 border ${statusTone}`}
              style={item.status === "inProgress" ? { color: th.accent } : undefined}
              data-testid="mywork-item-status">
              {t(`academy.mywork.status.${item.status}`)}
            </span>
            {item.inputLanguage && (
              <span className="text-[10px] tracking-[0.14em] uppercase text-bone/40">
                {t(`academy.mywork.writtenIn.${item.inputLanguage}`)}
              </span>
            )}
          </div>
          <p className="font-serif text-lg text-bone truncate max-w-[52ch]">
            {meta && <span className="text-bone/50">{t("academy.mywork.phase")} {meta.phaseOrder} · {t("academy.mywork.lesson")} {meta.order} · </span>}
            {title}
          </p>
          <p className="mt-1 text-[11px] text-fog/60">
            {t("academy.mywork.savedAt")}: {fmt(item.updatedAt || item.createdAt, lang)}
            {item.completedAt && <> · {t("academy.mywork.completedAt")} {fmt(item.completedAt, lang)}</>}
          </p>
        </div>
      </div>
      {open && item.preview && (
        <p className="px-5 pb-4 text-sm text-fog/85 font-reading leading-relaxed whitespace-pre-line" data-testid="mywork-item-preview">{item.preview}</p>
      )}
      <div className="px-5 pb-4 flex flex-wrap gap-x-5 gap-y-2 text-[10px] tracking-[0.18em] uppercase border-t border-white/5 pt-3">
        {canContinue && (
          <Link to={typeUrl(item)} className="hover:underline" style={{ color: th.light }} data-testid="mywork-action-continue">
            {t("academy.mywork.action.continue")}
          </Link>
        )}
        {item.preview && (
          <button onClick={() => setOpen(!open)} className="text-bone/60 hover:text-white" data-testid="mywork-action-view">
            {t("academy.mywork.action.view")}
          </button>
        )}
        {item.type === "lesson" && item.phaseSlug && (
          <Link to={lessonUrl(item)} className="text-bone/60 hover:text-white" data-testid="mywork-action-goto">
            {t("academy.mywork.action.gotoLesson")}
          </Link>
        )}
        {canDelete && !confirming && (
          <button onClick={() => setConfirming(true)} className="text-fog/40 hover:text-red-300" data-testid="mywork-action-delete">
            {t("academy.mywork.action.delete")}
          </button>
        )}
        {confirming && (
          <span className="flex items-center gap-3">
            <span className="text-red-300 normal-case tracking-normal">{t("academy.mywork.deleteConfirm")}</span>
            <button onClick={() => { setConfirming(false); onDelete(item); }} className="text-red-300 border border-red-400/40 px-2 py-1" data-testid="mywork-delete-confirm">✓</button>
            <button onClick={() => setConfirming(false)} className="text-fog/60 border border-white/15 px-2 py-1">✕</button>
          </span>
        )}
      </div>
    </div>
  );
}

export default function MyWork() {
  const { t, lang } = useT();
  const [items, setItems] = useState(null);
  const [lessonMeta, setLessonMeta] = useState({});
  const [disc, setDisc] = useState("all");
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");

  useSEO({
    title: `${t("academy.mywork.title")} | StoicOs.ai Academy`,
    description: t("academy.mywork.subtitle"),
    canonical: "https://stoicos.ai/academy/my-work",
  });

  const load = () =>
    fetch(`${API}/api/academy/my-work?logic_id=${logicLearnerId()}&physics_id=${physicsLearnerId()}&ethics_id=${ethicsLearnerId()}`)
      .then((r) => r.json()).then((d) => setItems(d.items || [])).catch(() => setItems([]));

  useEffect(() => {
    load();
    fetchLogicCourse().then((c) => {
      setLessonMeta((m) => {
        const nx = { ...m };
        (c.phases || []).forEach((p) => p.lessons.forEach((l) => { nx[`logic:${l.slug}`] = { ...l, phaseOrder: p.order }; }));
        return nx;
      });
    }).catch(() => {});
    fetchPhysicsCourse().then((c) => {
      setLessonMeta((m) => {
        const nx = { ...m };
        (c.phases || []).forEach((p) => p.lessons.forEach((l) => { nx[`physics:${l.slug}`] = { ...l, phaseOrder: p.order }; }));
        return nx;
      });
    }).catch(() => {});
    fetchEthicsCourse().then((c) => {
      setLessonMeta((m) => {
        const nx = { ...m };
        (c.phases || []).forEach((p) => p.lessons.forEach((l) => { nx[`ethics:${l.slug}`] = { ...l, phaseOrder: p.order }; }));
        return nx;
      });
    }).catch(() => {});
  }, []);

  const onDelete = async (item) => {
    if (item.type === "lesson") {
      if (item.discipline === "logic") await deleteLogicLessonWork(item.lessonSlug);
      else if (item.discipline === "ethics") await deleteEthicsLessonWork(item.lessonSlug);
      else await deletePhysicsLessonWork(item.lessonSlug);
    } else if (item.type === "record") {
      try {
        if (item.discipline === "logic") await deleteLogicRecord(item.id);
        else if (item.discipline === "ethics") await deleteEthicsRecord(item.id);
        else await deletePhysicsRecord(item.id);
      } catch { /* already gone */ }
    }
    load();
  };

  const filtered = useMemo(() => (items || []).filter((i) =>
    (disc === "all" || i.discipline === disc)
    && (status === "all" || i.status === status)
    && (type === "all" || i.type === type)), [items, disc, status, type]);

  const chip = (active, accent) =>
    `min-h-[38px] px-3 text-[10px] tracking-[0.18em] uppercase border transition-colors ${active
      ? "border-gold/70 text-gold bg-gold/10" : "border-white/15 text-bone/55 hover:text-white hover:border-white/35"}`;

  return (
    <main className="min-h-screen bg-ink px-6 sm:px-10 py-24" data-testid="my-work-page">
      <div className="max-w-4xl mx-auto">
        <DisciplineSwitcher active={null} />
        <nav aria-label="Breadcrumb" className="text-[10px] tracking-[0.25em] uppercase text-gold/70 mb-10">
          <Link to="/academy" className="hover:text-gold">{lang === "nl" ? "Academie" : "Academy"}</Link> / <span className="text-fog">{t("academy.mywork.title")}</span>
        </nav>
        <h1 className="font-serif text-4xl sm:text-5xl text-bone">{t("academy.mywork.title")}</h1>
        <p className="mt-3 font-serif italic text-lg text-gold/85">{t("academy.mywork.subtitle")}</p>
        <p className="mt-4 max-w-2xl text-[12px] text-fog/55 font-reading leading-relaxed" data-testid="mywork-storage-note">
          {t("academy.mywork.storageNote")}
        </p>

        {/* Filters */}
        <div className="mt-10 space-y-3" data-testid="mywork-filters">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] tracking-[0.25em] uppercase text-bone/40 w-24">{t("academy.mywork.filter.discipline")}</span>
            {["all", "logic", "physics", "ethics"].map((v) => (
              <button key={v} onClick={() => setDisc(v)} className={chip(disc === v)} data-testid={`mywork-filter-disc-${v}`}>
                {v === "all" ? t("academy.mywork.filter.all") : t(`academy.nav.name.${v}`)}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] tracking-[0.25em] uppercase text-bone/40 w-24">{t("academy.mywork.filter.status")}</span>
            {["all", "draft", "inProgress", "completed"].map((v) => (
              <button key={v} onClick={() => setStatus(v)} className={chip(status === v)} data-testid={`mywork-filter-status-${v}`}>
                {v === "all" ? t("academy.mywork.filter.all") : t(`academy.mywork.status.${v}`)}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] tracking-[0.25em] uppercase text-bone/40 w-24">{t("academy.mywork.filter.type")}</span>
            {["all", "lesson", "record", "practice", "evidence", "chamber"].map((v) => (
              <button key={v} onClick={() => setType(v)} className={chip(type === v)} data-testid={`mywork-filter-type-${v}`}>
                {v === "all" ? t("academy.mywork.filter.all") : t(`academy.mywork.type.${v}`)}
              </button>
            ))}
          </div>
        </div>

        <p className="mt-8 text-[10px] tracking-[0.25em] uppercase text-bone/35">{t("academy.mywork.sort")}</p>

        <div className="mt-4 space-y-4" data-testid="mywork-list">
          {items === null && <div className="h-32" />}
          {items !== null && filtered.length === 0 && (
            <p className="text-fog/50 font-reading italic py-10" data-testid="mywork-empty">{t("academy.mywork.empty")}</p>
          )}
          {filtered.map((item) => (
            <WorkItem key={`${item.type}-${item.id}-${item.lessonSlug || ""}`} item={item} lessonMeta={lessonMeta} onDelete={onDelete} />
          ))}
        </div>
      </div>
    </main>
  );
}
